/**
 * KeyboardShortcutsHelp — global shortcuts reference modal.
 *
 * Activated via ? key (when not in an input field) or Cmd+/ (Ctrl+/).
 * Shows all available keyboard shortcuts organized by category.
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd className={cn(
      "inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-md",
      "bg-slate-50 dark:bg-slate-800/40 text-[11px] font-mono font-semibold text-slate-900 dark:text-slate-100",
      "border border-slate-200/70 dark:border-slate-800 shadow-sm",
      className,
    )}>
      {children}
    </kbd>
  );
}

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: ShortcutItem[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "Global",
    shortcuts: [
      { keys: ["Cmd", "K"], description: "Open command palette" },
      { keys: ["?"], description: "Show keyboard shortcuts" },
      { keys: ["Cmd", "/"], description: "Show keyboard shortcuts" },
    ],
  },
  {
    title: "Navigation",
    shortcuts: [
      { keys: ["G", "H"], description: "Go to Dashboard" },
      { keys: ["G", "P"], description: "Go to Projects" },
      { keys: ["G", "T"], description: "Go to Team" },
      { keys: ["Esc"], description: "Close panel / dialog" },
    ],
  },
  {
    title: "Meeting Room",
    shortcuts: [
      { keys: ["M"], description: "Toggle microphone" },
      { keys: ["V"], description: "Toggle camera" },
      { keys: ["S"], description: "Share screen" },
      { keys: ["F"], description: "Mute/unmute Felix AI" },
      { keys: ["1"], description: "Felix panel" },
      { keys: ["2"], description: "Chat panel" },
      { keys: ["3"], description: "Participants panel" },
      { keys: ["4"], description: "Transcript panel" },
      { keys: ["5"], description: "Settings panel" },
      { keys: ["Esc"], description: "Close sidebar" },
    ],
  },
  {
    title: "Felix Chat",
    shortcuts: [
      { keys: ["Enter"], description: "Send message" },
      { keys: ["Shift", "Enter"], description: "New line" },
      { keys: ["Cmd", "Enter"], description: "Send with thinking" },
      // Phase 7 (UX v2): under the demotion flag, Felix becomes an
      // explicit "Ask Felix" pill in the topbar + ⌘K escape hatch
      // for unanticipated questions. The shortcut + the pill are the
      // only ways in — chat does not auto-open.
      { keys: ["Cmd", "K"], description: "Ask Felix anything (escape hatch)" },
    ],
  },
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

function formatKey(key: string): string {
  if (key === "Cmd") return isMac ? "\u2318" : "Ctrl";
  if (key === "Shift") return isMac ? "\u21E7" : "Shift";
  if (key === "Enter") return "\u23CE";
  if (key === "Esc") return "Esc";
  return key;
}

export function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't trigger in inputs
    const el = document.activeElement;
    if (el) {
      const tag = el.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if ((el as HTMLElement).isContentEditable) return;
    }

    // ? key (without modifiers)
    if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setOpen(true);
      return;
    }

    // Cmd+/ or Ctrl+/
    if (e.key === "/" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Press these keys to navigate and control the app quickly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.shortcuts.map((shortcut, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <span className="text-sm text-slate-600 dark:text-slate-300">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, ki) => (
                        <span key={ki} className="flex items-center gap-0.5">
                          {ki > 0 && <span className="text-[10px] text-slate-500 dark:text-slate-400 mx-0.5">+</span>}
                          <Kbd>{formatKey(key)}</Kbd>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 text-center">
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            Press <Kbd className="mx-0.5">Esc</Kbd> to close
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
